import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import Popup from 'reactjs-popup';
import { DEFAULT_CSV_COLUMNS } from '../constants/payoutManagerConstants';
import ColumnPicker from './ColumnPicker';

const axios = require('axios').default;
var _ = require('lodash');

const EventPayoutDownloadModal = ({ open, onClose, eventCsvPath, csvColumns, searchEventsPath }) => {
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_CSV_COLUMNS);
  const [includeCash, setIncludeCash] = useState(false);
  const [groupByPayout, setGroupByPayout] = useState(false);
  const [includeTotals, setIncludeTotals] = useState(false);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const listRef = useRef(null);

  const fetchEvents = useCallback((query, pageNum, append) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (query) params.append("q", query);
    params.append("page", pageNum.toString());

    const separator = searchEventsPath.includes("?") ? "&" : "?";
    axios.get(searchEventsPath + separator + params.toString())
      .then((response) => {
        const data = response.data;
        if (append) {
          setEvents((prev) => [...prev, ...data.events]);
        } else {
          setEvents(data.events);
        }
        setHasMore(data.has_more);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [searchEventsPath]);

  const debouncedSearchRef = useRef(null);

  useEffect(() => {
    debouncedSearchRef.current = _.debounce((query) => {
      setPage(1);
      fetchEvents(query, 1, false);
    }, 300);

    return () => debouncedSearchRef.current.cancel();
  }, [fetchEvents]);

  useEffect(() => {
    if (open) {
      fetchEvents("", 1, false);
    }
  }, [open, fetchEvents]);

  useEffect(() => {
    debouncedSearchRef.current(searchTerm);
    return () => debouncedSearchRef.current.cancel();
  }, [searchTerm]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchEvents(searchTerm, nextPage, true);
  };

  const toggleColumn = (key) => {
    setSelectedColumns((prev) =>
      prev.includes(key) ? prev.filter((col) => col !== key) : [...prev, key]
    );
  };

  const handleDownload = () => {
    if (!selectedEventId || selectedColumns.length === 0) return;

    const params = new URLSearchParams();
    params.append("event_id", selectedEventId);
    selectedColumns.forEach((col) => params.append("columns[]", col));
    params.append("include_cash", includeCash.toString());
    params.append("group_by_payout", groupByPayout.toString());
    params.append("include_totals", includeTotals.toString());

    const separator = eventCsvPath.includes("?") ? "&" : "?";
    window.location.href = eventCsvPath + separator + params.toString();
    onClose();
  };

  const canSubmit = selectedEventId && selectedColumns.length > 0;

  return (
    <Popup
      modal
      open={open}
      onClose={onClose}
      closeOnDocumentClick
      className="popup-modal"
      contentStyle={{
        textAlign: "left",
        border: "none",
        borderRadius: "8px",
        boxShadow: "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
        width: "600px",
        maxHeight: "85vh",
        padding: "24px",
        overflowY: "auto"
      }}
    >
      {(close) => (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Event Payout Export</h5>
            <a href="#" onClick={(e) => { e.preventDefault(); close(); }}
              className="h4 mb-0 text-muted text-decoration-none">
              &times;
            </a>
          </div>

          <div className="mb-3">
            <label className="d-block mb-2"><strong>Search Event</strong></label>
            <input
              type="text"
              className="form-control mb-2"
              placeholder="Search by event name or venue..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div ref={listRef} className="border rounded" style={{ maxHeight: "250px", overflowY: "auto" }}>
              {!loading && events.length === 0 && (
                <div className="px-3 py-2 text-muted small">
                  {searchTerm ? "No events found" : "Type to search events..."}
                </div>
              )}
              {events.map((event) => (
                <label
                  key={event.id}
                  className={`d-flex align-items-center px-3 py-2 mb-0 border-bottom cursor-pointer ${selectedEventId === event.id ? "bg-light" : ""}`}
                >
                  <input
                    type="radio"
                    name="eventSelection"
                    className="mr-3"
                    checked={selectedEventId === event.id}
                    onChange={() => setSelectedEventId(event.id)}
                  />
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="small">
                      <strong>{event.title}</strong>
                    </div>
                    <div className="small text-muted">
                      {event.venue}{event.date ? ` \u2014 ${event.date}` : ""}
                    </div>
                  </div>
                </label>
              ))}
              {loading && (
                <div className="px-3 py-2 text-muted text-center small">
                  Loading...
                </div>
              )}
              {hasMore && !loading && (
                <div className="text-center py-2">
                  <a href="#" onClick={(e) => { e.preventDefault(); loadMore(); }} className="small">
                    Load more events
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="mb-3">
            <label className="d-block mb-2"><strong>Options</strong></label>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="eventIncludeCash"
                checked={includeCash}
                onChange={() => setIncludeCash(!includeCash)}
              />
              <label className="form-check-label" htmlFor="eventIncludeCash">
                Include cash transactions
              </label>
            </div>
            <div className="form-check mt-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="eventGroupByPayout"
                checked={groupByPayout}
                onChange={() => setGroupByPayout(!groupByPayout)}
              />
              <label className="form-check-label" htmlFor="eventGroupByPayout">
                Group by payout (show totals per payout)
              </label>
            </div>
            <div className="form-check mt-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="eventIncludeTotals"
                checked={includeTotals}
                onChange={() => setIncludeTotals(!includeTotals)}
              />
              <label className="form-check-label" htmlFor="eventIncludeTotals">
                Include totals row
              </label>
            </div>
          </div>

          <ColumnPicker
            csvColumns={csvColumns}
            selectedColumns={selectedColumns}
            onToggle={toggleColumn}
            onSelectAll={() => setSelectedColumns(csvColumns.map((col) => col.key))}
            onDeselectAll={() => setSelectedColumns([])}
            collapsible
            showColumnPicker={showColumnPicker}
            onToggleVisibility={() => setShowColumnPicker(!showColumnPicker)}
          />

          <div className="d-flex justify-content-end">
            <button className="btn btn-setlist-secondary mr-2" onClick={close}>
              Cancel
            </button>
            <button
              className="btn btn-setlist-primary"
              onClick={handleDownload}
              disabled={!canSubmit}
            >
              Download Event CSV
            </button>
          </div>
        </div>
      )}
    </Popup>
  );
};

EventPayoutDownloadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  eventCsvPath: PropTypes.string.isRequired,
  csvColumns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  searchEventsPath: PropTypes.string.isRequired
};

export default EventPayoutDownloadModal;
